/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useDispatch, useSelector } from 'react-redux';
import {
  selectTrendSymmetricComparisonToggle,
  selectTrendTimeDateComparison,
  selectTrendTimeAlignmentToggle,
  selectTrendSegmentChartBreakdown,
  selectIsSegmentDetailed,
  selectTrendSegmentFocusOn,
  selectIsForecast,
  selectTrendStartDate,
  selectTrendEndDate,
  selectIsCumulative,
  selectMovingAverage,
  selectTrendTimeWeeklyView,
  selectIsMovingAverage,
  selectIsPercentageChange,
  selectTrendTimeStartDate,
  selectTrendTimeEndDate,
  selectTrendGranularity,
  selectTrendData,
  selectTrendDataResponseId,
} from 'modules/dashboard/components/tab-container/trend-tabs/selectors';
import {
  selectHotelName,
  selectLastPartialBookingDate,
  selectLocalCurrency,
  selectPartialHours,
  selectPmsSync,
  selectResetBookingDate,
  selectTrendBookingDate,
  selectTrendTargetDate,
  selectVisualFilters,
} from 'modules/dashboard/selectors';
import { useEffect, useMemo, useRef, useState } from 'react';
import MuiTabular from 'modules/common/components/mui-tabular';
import { Card, Grid, IconButton, Typography } from '@mui/material';
import {
  selectAuthUser,
  selectOrganizationId,
  selectSessionId,
} from 'modules/common/auth/selectors';
import { EXPORT_BUTTON_STYLE } from 'modules/common/components/mui-tabular/style';
import { EXPORT_OPTIONS } from 'modules/dashboard/constants';
import DownloadIcon from '@mui/icons-material/Download';
import { AssistantIcon } from 'modules/common/components';
import { useDebouncedCallback } from 'use-debounce';
import styles from 'modules/dashboard/components/tab-container/tab-container.module.scss';
import CollapseIcon from 'modules/common/components/collapse-icon';
import { ENVIRONMENT } from 'config';
import {
  TREND_CUMULATIVE_FORECAST_TABLE_COLUMNS,
  TREND_CUMULATIVE_TABLE_COLUMNS,
  TREND_MOVING_AVERAGE_TABLE_COLUMNS,
  TREND_PERCENTAGE_TABLE_COLUMNS,
  TREND_TABLE_COLUMNS,
  TREND_WEEKLY_FORECAST_TABLE_COLUMNS,
  TREND_WEEKLY_TABLE_COLUMNS,
} from '../../../../constants';
import {
  buildTrendFilters,
  buildTrendHeaderData,
  getColumns,
  getExportFileName,
  handleCeruleanLogging,
} from '../../../../functions';
import TabularFilterHeader from '../../filter-header';
import { trendActions } from '../../../../slice';

/**
 * Trend Time tabular visual report with filters
 */

const MuiTrendTimeTabularWidget = ({ setIsTimeLoaded, isLoaded }) => {
  const bookingDate = useSelector(selectTrendBookingDate);
  const currencyType = useSelector(selectLocalCurrency);
  const hotels = useSelector(selectHotelName);
  const targetStartDate = useSelector(selectTrendStartDate);
  const targetEndDate = useSelector(selectTrendEndDate);
  const alignmentToggle = useSelector(selectTrendTimeAlignmentToggle);
  const symmetricComparisonToggle = useSelector(selectTrendSymmetricComparisonToggle);
  const dateComparison = useSelector(selectTrendTimeDateComparison);
  const pms = useSelector(selectPmsSync);
  const visualFilters = useSelector(selectVisualFilters);
  const chartBreakdown = useSelector(selectTrendSegmentChartBreakdown);
  const isDetailed = useSelector(selectIsSegmentDetailed);
  const focusOn = useSelector(selectTrendSegmentFocusOn);
  const isForecast = useSelector(selectIsForecast);
  const isCumulative = useSelector(selectIsCumulative);
  const organizationId = useSelector(selectOrganizationId);
  const movingAverage = useSelector(selectMovingAverage);
  const isMovingAverage = useSelector(selectIsMovingAverage);
  const isWeeklyView = useSelector(selectTrendTimeWeeklyView);
  const isPercentageChange = useSelector(selectIsPercentageChange);
  const endDate = useSelector(selectTrendTimeEndDate);
  const startDate = useSelector(selectTrendTimeStartDate);
  const resetBookingDate = useSelector(selectResetBookingDate);
  const user = useSelector(selectAuthUser);
  const sessionId = useSelector(selectSessionId);
  const trendData = useSelector(selectTrendData);
  const trendDataResponseId = useSelector(selectTrendDataResponseId);
  const trendGranularity = useSelector(selectTrendGranularity);
  const partialHours = useSelector(selectPartialHours);
  const lastBookingDate = useSelector(selectLastPartialBookingDate);
  const targetDate = useSelector(selectTrendTargetDate);
  const dispatch = useDispatch();
  //
  const tableRef = useRef();
  //
  const [exportType, setExportType] = useState(EXPORT_OPTIONS.DATA_EXPORT);
  const [isDisplay, setIsDisplay] = useState(true);
  //
  const [isProcessing, setIsProcessing] = useState(false);
  const [exportData, setExportData] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  // Debounce the request to avoid excessive calls
  const fetchData = useDebouncedCallback(async () => {
    // Build the filters for the API request
    const filters = buildTrendFilters({
      bookingDate,
      targetStartDate,
      targetEndDate,
      alignmentToggle,
      symmetricComparisonToggle,
      dateComparison,
      currencyType,
      hotels,
      pms,
      chartBreakdown,
      visualFilters,
      isDetailed,
      focusOn,
      isForecast,
      movingAverage,
      isMovingAverage,
      startDate,
      endDate,
      resetBookingDate,
      trendGranularity,
      partialHours,
      lastBookingDate,
      originalTargetDate: targetDate,
      isWeeklyAdjusted: isWeeklyView && !isCumulative,
    });
    //
    const columns = getColumns({
      isPercentageChange,
      isMovingAverage,
      isCumulative,
      isWeeklyView,
      isForecast,
    });
    //
    let start;
    try {
      setIsTimeLoaded(false);
      // Log the request time for performance monitoring
      handleCeruleanLogging({
        message: `Trend Time Tabular Filters: ${JSON.stringify(filters)}`,
        sessionId,
        user,
        eventType: 'Cerulean Filters',
      });
      handleCeruleanLogging({
        message: `Trend Tabular Request start: ${new Date()}`,
        sessionId,
        user,
        eventType: 'Cerulean Loading',
      });
      start = performance.now();
      //
      setExportData({
        columns,
        filters,
        title: 'mui-tabular-trend-time',
      });
      dispatch(
        trendActions.getTrendData({
          organizationId,
          columns,
          filters,
        })
      );
    } catch (error) {
      handleCeruleanLogging({
        message: `Error fetching trend time tabular data: ${error}`,
        sessionId,
        user,
        eventType: 'Cerulean Error',
      });
    } finally {
      // Log the time taken for the request
      const end = performance.now();
      handleCeruleanLogging({
        message: `Trend Tabular Request finished: ${new Date()}`,
        sessionId,
        user,
        eventType: 'Cerulean Loaded',
      });
      handleCeruleanLogging({
        message: `Trend Tabular Request time: ${((end - start) / 1000).toFixed(2)} seconds`,
        sessionId,
        user,
        eventType: 'Cerulean Request Time',
      });
    }
  }, 500);
  //
  useEffect(() => {
    // loaded when trend data is available or a request is completed, also after an error
    if (trendData || trendDataResponseId > 0) {
      setIsTimeLoaded(true);
    }
  }, [trendData, trendDataResponseId]);
  //
  useEffect(() => {
    if (isDisplay) {
      fetchData();
    } else {
      // a visual that is not displayed has nothing to load
      setIsTimeLoaded(true);
    }
  }, [
    bookingDate,
    currencyType,
    hotels,
    alignmentToggle,
    symmetricComparisonToggle,
    dateComparison,
    setIsTimeLoaded,
    targetStartDate,
    targetEndDate,
    pms,
    focusOn,
    isCumulative,
    isForecast,
    movingAverage,
    isWeeklyView,
    isMovingAverage,
    isPercentageChange,
    startDate,
    endDate,
    trendGranularity,
    partialHours,
    lastBookingDate,
    isDisplay,
    targetDate,
  ]);
  //
  const headerData = useMemo(
    () =>
      buildTrendHeaderData({
        hotelName: hotels?.label,
        bookingDate,
        targetStartDate,
        targetEndDate,
        alignmentToggle,
        symmetricComparisonToggle,
        currencyType,
        aligned: alignmentToggle,
        pmsSync: pms,
        dateComparison,
        startDate,
        endDate,
        isCumulative,
        isPercentageChange,
        isWeeklyView,
        isMovingAverage,
        resetBookingDate,
        partialHours,
        lastBookingDate,
        originalTargetDate: targetDate,
      }),
    [
      hotels,
      bookingDate,
      targetEndDate,
      targetStartDate,
      alignmentToggle,
      startDate,
      endDate,
      symmetricComparisonToggle,
      currencyType,
      pms,
      dateComparison,
      isCumulative,
      isPercentageChange,
      isWeeklyView,
      isMovingAverage,
      resetBookingDate,
      partialHours,
      lastBookingDate,
      targetDate,
      trendGranularity,
    ]
  );
  const tableSchema = () => {
    if (isCumulative && isForecast) {
      return TREND_CUMULATIVE_FORECAST_TABLE_COLUMNS;
    }
    if (isWeeklyView && isForecast) {
      return TREND_WEEKLY_FORECAST_TABLE_COLUMNS;
    }
    if (isMovingAverage && isPercentageChange) {
      return TREND_PERCENTAGE_TABLE_COLUMNS;
    }
    if (isCumulative) {
      return TREND_CUMULATIVE_TABLE_COLUMNS;
    }
    if (isWeeklyView) {
      return TREND_WEEKLY_TABLE_COLUMNS;
    }
    if (isMovingAverage) {
      return TREND_MOVING_AVERAGE_TABLE_COLUMNS;
    }
    return TREND_TABLE_COLUMNS;
  };
  const fileName = useMemo(
    () =>
      getExportFileName({
        isCumulative,
        isMovingAverage,
        isPercentageChange,
        isWeeklyView,
      }),
    [isCumulative, isMovingAverage, isPercentageChange, isWeeklyView]
  );
  //
  const onClick = () => {
    setIsDisplay(!isDisplay);
  };
  //
  return (
    <Grid
      container
      direction="row"
      justifyContent="center"
      alignItems="flex-start"
      className={styles.mainGrid}
      mb={10}
    >
      <Card
        sx={{
          width: '100%',
          margin: '1rem 8rem',
        }}
      >
        <Grid
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          height={40}
          width="100%"
        >
          <Grid item pl={2}>
            {headerData?.title}
          </Grid>
          <Grid item>
            <Grid
              item
              container
              direction="row"
              justifyContent="flex-end"
              alignItems="center"
              width="100%"
            >
              {ENVIRONMENT.IS_VISUAL_INTERPRETER_ENABLED?.toLowerCase() === 'true' && (
                <Grid item mt={1} mr={4}>
                  <AssistantIcon
                    exportData={exportData}
                    visualName={headerData?.title}
                    isProcessing={isExporting}
                    setIsProcessing={setIsExporting}
                    disabled={isProcessing || !isLoaded || isExporting || !isDisplay}
                    isCerulean
                    getExcelExportData={tableRef?.current?.getExcelData}
                    exportType={exportType}
                    setExportType={setExportType}
                  />
                </Grid>
              )}

              <IconButton
                size="medium"
                onClick={() => setIsProcessing(true)}
                disabled={isProcessing || !isLoaded || isExporting || !isDisplay}
                sx={EXPORT_BUTTON_STYLE}
                id="trend-time-js-export-button"
              >
                <Typography
                  sx={{
                    mr: 1,
                  }}
                >
                  Export Data
                </Typography>
                <DownloadIcon />
              </IconButton>

              <CollapseIcon isDisplay={isDisplay} onClick={onClick} />
            </Grid>
          </Grid>
        </Grid>
        {isDisplay &&
          (!trendData ? (
            <Typography fontWeight={500} pb={4} textAlign="center">
              Loading Data...
            </Typography>
          ) : (
            <MuiTabular
              ref={tableRef}
              columns={tableSchema()}
              isProcessing={isProcessing}
              isLoadingData={!isLoaded}
              setIsProcessing={setIsProcessing}
              isAggregate={!(isMovingAverage || isPercentageChange)}
              headerComponent={
                <TabularFilterHeader
                  title=""
                  hotelName={headerData?.hotelName}
                  isHotelSet={Boolean(hotels?.hotels)}
                  sections={headerData?.sections}
                />
              }
              data={trendData}
              headerFiltersInfo={headerData}
              customBackgroundStyle={{
                paddingLeft: 3,
                maxWidth: { md: '72vw', lg: '76vw' },
                paddingBottom: 8,
              }}
              fileName={fileName}
              primaryTextColor={isPercentageChange ? '#000' : undefined}
              secondaryTextColor={isPercentageChange ? '#000' : undefined}
            />
          ))}
      </Card>
    </Grid>
  );
};
//
export default MuiTrendTimeTabularWidget;
